import _ from 'lodash'
import React from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Col,
  Row,
  Button,
  DetailList,
  Card,
  FormInput,
  FormInputText,
  FormInputSelect,
  FormInputTextArea,
  FormInputCheckbox,
  ButtonContainer,
  List,
  ListItem
} from 'camino-react'

import { cloudhelpPatch, cloudhelpPost } from 'Utilities/CloudHelp'

const ServiceCategoryField = ({ addServiceCategory, serviceCategories }) => (
  <FormInputSelect
    value=""
    onValue={addServiceCategory}
    options={serviceCategories}
    clearable={false}
    searchable
  />
)

ServiceCategoryField.propTypes = {
  addServiceCategory: PropTypes.func.isRequired,
  serviceCategories: PropTypes.arrayOf(PropTypes.object).isRequired
}

class TicketTypeForm extends React.Component {
  constructor (props) {
    super(props)

    this.submit = this.submit.bind(this)
    this.addServiceCategory = this.addServiceCategory.bind(this)
    const serviceCategoriesAsOptions = serviceCategories => _.map(
      serviceCategories,
      (serviceCategory) => {
        let label = serviceCategory.name
        if (serviceCategory.parent_name) {
          label = `${serviceCategory.parent_name}: ${label}`
        }

        return ({
          label,
          value: serviceCategory.id,
          parent_id: serviceCategory.parent_id
        })
      }
    )

    const childServiceCategories = _.filter(
      props.ticketType.service_categories,
      serviceCategory => !_.isNil(serviceCategory.parent_id)
    )

    const chosenServiceCategories = _.sortBy(
      serviceCategoriesAsOptions(childServiceCategories),
      'label'
    )

    const serviceCategoryOptions = _(
      serviceCategoriesAsOptions(props.serviceCategories)
    ).differenceBy(chosenServiceCategories, 'value').sortBy('label').value()

    this.state = {
      fields: {
        name: props.ticketType.name,
        description: props.ticketType.description,
        internal: props.ticketType.internal,
        region_id: props.ticketType.region_id
      },
      chosenServiceCategories,
      submitButton: {
        text: 'Save Ticket Type',
        disabled: false
      },
      showAlert: false,
      alertText: '',
      alertType: 'info',
      serviceCategoryOptions
    }
  }

  handleFieldValue = (fieldName, value) => {
    this.setState((prevState) => {
      const { fields } = prevState
      const newFields = Object.assign({}, fields, { [fieldName]: value })
      return (
        { fields: newFields }
      )
    })
  }

  addServiceCategory (value) {
    this.setState(({
      chosenServiceCategories: prevChosenServiceCategories,
      serviceCategoryOptions: prevServiceCategoryOptions
    }) => {
      const option = _.find(prevServiceCategoryOptions, ({ value }))
      const chosenServiceCategories = _.concat(prevChosenServiceCategories, option)

      return {
        chosenServiceCategories: _.sortBy(chosenServiceCategories, 'label'),
        serviceCategoryOptions: _.without(prevServiceCategoryOptions, option)
      }
    })
  }

  removeServiceCategory (option) {
    this.setState((prevState) => {
      const serviceCategoryOptions = _.concat(prevState.serviceCategoryOptions, option)

      return {
        chosenServiceCategories: _.without(prevState.chosenServiceCategories, option),
        serviceCategoryOptions: _.sortBy(serviceCategoryOptions, 'label')
      }
    })
  }

  submit () {
    const {
      fields: {
        name,
        description,
        internal,
        region_id
      },
      chosenServiceCategories
    } = this.state

    this.setState({
      submitButton: {
        text: 'Saving Ticket Type',
        disabled: true
      }
    })

    const chosenServiceCategoryIds = _.compact(
      _.union(
        _.map(chosenServiceCategories, 'value'),
        _.map(chosenServiceCategories, 'parent_id')
      )
    )

    this.makeRequest({
      ticket_type: {
        name,
        description,
        internal,
        ownerable_type: 'Region',
        ownerable_id: region_id,
        service_category_ids: chosenServiceCategoryIds
      }
    })
      .then((response) => {
        this.setState({
          submitButton: {
            text: 'Saved Ticket Type',
            disabled: false
          },
          errors: [],
          alertText: response.data.success_message,
          showAlert: true,
          alertType: 'success'
        })

        window.location = response.data.redirect_url
      })
      .catch((response) => {
        const errors = _.get(response, 'response.data.error_messages', [])
        const fieldsWithErrors = _.get(response, 'response.data.fields_with_errors', [])

        this.setState({
          submitButton: {
            text: 'Save Ticket Type',
            disabled: false
          },
          errors,
          fieldsWithErrors,
          alertText: null,
          showAlert: true,
          alertType: 'error'
        })
      })
  }

  makeRequest (data) {
    const { formEndpoint } = this.props

    if (formEndpoint.method === 'POST') { return cloudhelpPost(formEndpoint.path, data) }

    return cloudhelpPatch(formEndpoint.path, data)
  }

  render () {
    const {
      fields: {
        name,
        description,
        internal,
        region_id
      },
      chosenServiceCategories,
      showAlert,
      alertText,
      alertType,
      submitButton: {
        text: submitText,
        disabled: submitDisabled
      },
      serviceCategoryOptions,
      errors
    } = this.state

    const {
      cancelPath,
      regionOptions,
      ticketType: { ownerable_type }
    } = this.props

    return (
      <Row>
        <Col>
          { (alertText || errors) && (
            <Alert type={alertType} title={_.some(errors) ? 'Please correct the errors below' : ''}>
              {alertText || errors}
            </Alert>
          ) }
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Ticket type details',
                    content: 'If made internal, Name and Description will be shown to the customer.'
                  }
                ]}
              />
            </Col>
            <Col>
              <Card allowOverflow>
                <FormInput label="Name">
                  <FormInputText
                    value={name}
                    onValue={value => (this.handleFieldValue('name', value))}
                  />
                </FormInput>
                <FormInput label="Description">
                  <FormInputTextArea
                    value={description}
                    rows={5}
                    onValue={value => (this.handleFieldValue('description', value))}
                  />
                </FormInput>
                { ownerable_type !== 'Client' && (
                  <FormInput label="Region">
                    <FormInputSelect
                      value={_.find(regionOptions, ['value', region_id])}
                      onValue={option => (this.handleFieldValue('region_id', option))}
                      options={regionOptions}
                      searchable
                      placeholder="Global"
                    />
                  </FormInput>
                )}
                <FormInput label="Internal">
                  <FormInputCheckbox
                    value={internal}
                    rows={5}
                    onValue={value => (this.handleFieldValue('internal', value))}
                  />
                </FormInput>
              </Card>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col>
              <DetailList
                list={[
                  {
                    title: 'Service categories',
                    content: (
                      <>
                        Service categories can be added here to restrict which category can be used for
                        which type.
                        <br />
                        <br />
                        If no categories are assigned the type will use all available service categories.
                      </>
                    )
                  }
                ]}
              />
            </Col>
            <Col>
              <Card allowOverflow>
                <FormInput>
                  <ServiceCategoryField
                    serviceCategories={serviceCategoryOptions}
                    addServiceCategory={this.addServiceCategory}
                    removeServiceCategory={this.removeServiceCategory}
                  />
                  {_.some(chosenServiceCategories) && (
                    <List>
                      {_.map(chosenServiceCategories, serviceCategoryOption => (
                        <ListItem>
                          {serviceCategoryOption.label}
                          <button
                            type="button"
                            style={{ border: 'none', background: 'transparent', outline: 'none' }}
                            className="lnr lnr-trash2 lnr--hover-blue"
                            onClick={() => this.removeServiceCategory(serviceCategoryOption)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </FormInput>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col>
              <ButtonContainer center spaced>
                <a href={cancelPath} className="button button--outline">
                  Cancel
                </a>
                <Button onClick={() => this.submit()} disabled={submitDisabled}>
                  {submitText}
                </Button>
              </ButtonContainer>
            </Col>
          </Row>
        </Col>
      </Row>
    )
  }
}

TicketTypeForm.propTypes = {
  ticketType: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    internal: PropTypes.bool,
    region_id: PropTypes.number,
    service_categories: PropTypes.array
  }).isRequired,
  formEndpoint: PropTypes.shape({
    method: PropTypes.string,
    path: PropTypes.string
  }).isRequired,
  serviceCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
  cancelPath: PropTypes.string.isRequired,
  isPersisted: PropTypes.bool.isRequired
}

export default TicketTypeForm
